import { lazy } from 'react';
import ConfigProvider from 'antd/es/config-provider';
import { Route } from 'react-router-dom-v5-compat';

import { OpApp } from 'new-components/DLS/OpApp/OpApp';
import { OpAppScaffold } from 'new-components/OpAppScaffold/OpAppScaffold';
import { getBasenameForApp } from 'utils/getBasenameForApp';
import { OpAuthenticatedRoute } from 'new-components/OpAppScaffold/components/OpAuthenticatedRoute';
import { OpScopeGuard } from 'new-components/OpScopeGuard/OpScopeGuard';
import NotFoundPage from 'pages/NotFoundPage/NotFoundPage';
import { scopeCodesToDescriptions } from 'utils/scopeDescriptions';
import { AlertWrapper } from 'containers/AlertWrapper/AlertWrapper';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useThemeObserver } from 'utils/customHooks/useThemeObserver';
import { OpWithoutSidebar } from 'new-components/OpAppScaffold/components/OpWithoutSidebar';

import { OrgIdOutlet } from '../app/new-components/OpAppScaffold/components/OrgIdOutlet';

import {
  sosHomeMenuItem,
  sosSitesMenuItem,
  sosAdminMenuItem,
  sidebarItems,
  sosCamerasMenuItem,
} from './constants/sidebarItems';
import {
  baseRoute,
  sosAdminRoute,
  sosCamerasRoute,
  sosHomeRoute,
  sosSitesRoute,
  sosWelcomeRoute,
} from './constants/routes';
import { SosWelcomePageGuard } from './components/SosWelcomePageGuard/SosWelcomePageGuard';

import './App.scss';

const SosSitesPage = lazy(() => import('./pages/SosSitesPage/SosSitesPage'));
const SosAdminPage = lazy(() => import('./pages/SosAdminPage/SosAdminPage'));
const SosWelcomePage = lazy(
  () => import('./pages/SosWelcomePage/SosWelcomePage'),
);
const SosHomePage = lazy(() => import('./pages/SosHomePage/SosHomePage'));
const SosCamerasPage = lazy(
  () => import('./pages/SosCamerasPage/SosCamerasPage'),
);

/**
 * @component
 * @description Component that wraps the sos app and holds the top level functionality.
 */
export const SosAppScaffold = () => {
  // Gets the correct basename for the app based on the current environment
  const basename = getBasenameForApp('sos');

  const { identityId } = useIdentityAndUser();
  const { theme } = useThemeObserver({ identityId });

  return (
    <ConfigProvider theme={theme}>
      <OpApp className="op-sos-app">
        <AlertWrapper />
        <OpAppScaffold
          appScopePrefix="sos"
          basename={basename}
          homePath={`${baseRoute}/${sosHomeRoute}`}
          sidebarItems={sidebarItems()}
          title="SOS"
        >
          {/* Routes requiring an orgId should be placed inside this route */}
          <Route
            path={`${baseRoute}/`}
            element={<OrgIdOutlet homePage={sosHomeRoute} />}
          >
            {/* All Routes that require login should be inside here */}
            <Route element={<OpAuthenticatedRoute />}>
              {/* Welcome page is always accessible and doesn't require facility org */}
              <Route
                path={sosWelcomeRoute}
                element={
                  <OpWithoutSidebar>
                    <SosWelcomePage />
                  </OpWithoutSidebar>
                }
              />

              {/* Routes that require facility org should be wrapped in WelcomePageGuard */}
              <Route element={<SosWelcomePageGuard />}>
                <Route
                  path={sosHomeRoute}
                  element={
                    <OpScopeGuard
                      allowedScopes={sosHomeMenuItem().scope ?? []}
                      element={<SosHomePage />}
                      fallback={
                        <NotFoundPage
                          routeDescription={sosHomeMenuItem().title}
                          missingScopeDescriptions={scopeCodesToDescriptions(
                            sosHomeMenuItem().scope ?? [],
                          )}
                        />
                      }
                    />
                  }
                />
                <Route
                  path={sosSitesRoute}
                  element={
                    <OpScopeGuard
                      allowedScopes={sosSitesMenuItem().scope ?? []}
                      element={<SosSitesPage />}
                      fallback={
                        <NotFoundPage
                          routeDescription={sosSitesMenuItem().title}
                          missingScopeDescriptions={scopeCodesToDescriptions(
                            sosSitesMenuItem().scope ?? [],
                          )}
                        />
                      }
                    />
                  }
                />
                <Route
                  path={sosCamerasRoute}
                  element={
                    <OpScopeGuard
                      allowedScopes={sosCamerasMenuItem().scope ?? []}
                      element={<SosCamerasPage />}
                      fallback={
                        <NotFoundPage
                          routeDescription={sosCamerasMenuItem().title}
                          missingScopeDescriptions={scopeCodesToDescriptions(
                            sosCamerasMenuItem().scope ?? [],
                          )}
                        />
                      }
                    />
                  }
                />
                <Route
                  path={sosAdminRoute}
                  element={
                    <OpScopeGuard
                      allowedScopes={sosAdminMenuItem().scope ?? []}
                      element={<SosAdminPage />}
                      fallback={
                        <NotFoundPage
                          routeDescription={sosAdminMenuItem().title}
                          missingScopeDescriptions={scopeCodesToDescriptions(
                            sosAdminMenuItem().scope ?? [],
                          )}
                        />
                      }
                    />
                  }
                />
              </Route>
            </Route>
          </Route>
        </OpAppScaffold>
      </OpApp>
    </ConfigProvider>
  );
};
